import React from 'react';
import { MapPin, ArrowDown, Linkedin, Mail } from 'lucide-react';
import { profileData, statsData } from '../data/mock';
import AcademyFlagBanner from './AcademyFlagBanner';

const Hero = () => {
  const scrollToAbout = () => {
    const element = document.querySelector('#about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="min-h-screen bg-[#faf6ee] pt-20 relative overflow-hidden" data-testid="hero-section">
      <AcademyFlagBanner />
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 right-20 w-72 h-72 bg-gold-400/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-gold-400/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="min-h-[calc(100vh-5rem)] flex flex-col justify-center py-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="flex items-center gap-2 text-[#8b94a3] mb-6">
                <MapPin size={18} strokeWidth={2.5} className="text-gold-400" />
                <span className="text-sm font-medium tracking-wide">{profileData.location}</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#152846] leading-tight mb-6" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="hero-name">
                {profileData.name}
              </h1>

              <ul className="text-lg text-[#5c6878] mb-6 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-gold-400 mt-1.5">&#8226;</span>
                  <span>
                    <span className="font-bold text-gold-400">Chief Executive Officer</span>
                    <br />
                    ITHR Technologies Consulting LLC
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gold-400 mt-1.5">&#8226;</span>
                  <span>
                    <span className="font-bold text-gold-400">Business Head IT & ITES - (Fractional)</span>
                    <br />
                    ERAM Digital, Subsidiary of ERAM Holdings
                  </span>
                </li>
              </ul>

              <p className="text-lg text-[#5c6878] leading-relaxed mb-8 max-w-xl font-semibold italic">
                {profileData.tagline}
              </p>

              <div className="flex flex-wrap gap-4 mb-12">
                <a
                  href={`mailto:${profileData.email}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gold-400 text-black font-semibold rounded-full hover:bg-gold-300 transition-all duration-300 hover:shadow-lg hover:shadow-gold-400/20 hover:-translate-y-0.5"
                  data-testid="hero-contact-btn"
                >
                  <Mail size={18} strokeWidth={2.5} />
                  Contact Me
                </a>
                <a
                  href={profileData.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-gold-400 text-gold-400 font-semibold rounded-full hover:bg-gold-400 hover:text-black transition-all duration-300"
                  data-testid="hero-linkedin-btn"
                >
                  <Linkedin size={18} strokeWidth={2.5} />
                  LinkedIn Profile
                </a>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {statsData.map((stat, index) => (
                  <div key={index} className="text-center sm:text-left" data-testid={`stat-${index}`}>
                    <div className="text-3xl font-bold text-gold-400 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {stat.value}
                    </div>
                    <div className="text-sm text-[#8b94a3] font-medium">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-gold-400/20 to-gold-700/10 rounded-2xl blur-2xl opacity-40" />
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-gold-400/10 rounded-full" />
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gold-400/5 rounded-full" />
                <div className="relative w-72 sm:w-80 lg:w-[420px] rounded-2xl overflow-hidden border-4 border-gold-400/30 shadow-2xl shadow-gold-400/10">
                  <img
                    src={profileData.photo}
                    alt={profileData.name}
                    className="w-full h-auto object-contain"
                    data-testid="hero-photo"
                  />
                </div>
                <a
                  href="https://www.linkedin.com/posts/srijit-nair-ithr_imc-individual-member-this-digital-credential-share-7475843230769704960-xJ69/"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="IMC — International Mentoring Center · Individual Member"
                  className="absolute -bottom-5 -right-5 sm:-bottom-6 sm:-right-6 z-20 group"
                  data-testid="hero-imc-badge"
                >
                  <div className="relative w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-full bg-white shadow-2xl shadow-black/30 ring-4 ring-[#faf6ee] hover:ring-gold-400/40 transition-all duration-300 hover:scale-105 hover:rotate-3 overflow-hidden">
                    <img
                      src="/badges/imc-member.png"
                      alt="IMC Individual Member credential"
                      className="w-full h-full object-contain p-1"
                    />
                  </div>
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap px-3 py-1.5 bg-[#152846] text-[#faf6ee] text-xs font-semibold rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg pointer-events-none">
                    IMC Member · Verified
                  </span>
                </a>
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-12">
            <button
              onClick={scrollToAbout}
              className="p-3 text-[#aab2bf] hover:text-gold-400 transition-colors animate-bounce"
              aria-label="Scroll to about section"
              data-testid="scroll-down-btn"
            >
              <ArrowDown size={24} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
